import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CTA from "@/components/CTA";
import { Phone, Brain, Calendar, Shield, MessageSquare, CheckCircle } from "lucide-react";

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              How <span className="text-gradient">CallChat</span> Works
            </h1>
            <p className="text-lg text-muted-foreground">
              From the moment a customer calls to the confirmed booking, here's exactly what happens behind the scenes.
            </p>
          </div>
        </div>
      </section>

      {/* Step by Step */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="flex flex-col md:flex-row gap-8 mb-16">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Phone className="w-8 h-8 text-primary-foreground" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium text-primary">Step 1</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Customer Calls Your Number</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  When a customer calls your business number, the call is instantly routed to CallChat. There's no delay—your AI assistant picks up immediately, even if it's 3 AM on a Sunday.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Instant pickup
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    24/7 availability
                  </span>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col md:flex-row gap-8 mb-16">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Brain className="w-8 h-8 text-primary-foreground" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium text-primary">Step 2</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <h2 className="text-2xl font-bold mb-4">AI Understands the Intent</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  CallChat uses advanced natural language processing to understand what the caller needs. Whether they're asking about your hours, services, or want to book an appointment—the AI gets it right.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Natural conversations
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Multilingual
                  </span>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col md:flex-row gap-8 mb-16">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-primary-foreground" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium text-primary">Step 3</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Bookings Are Confirmed Automatically</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If the caller wants to book an appointment, CallChat checks your calendar in real-time, suggests available slots, and confirms the booking. The customer receives an email or SMS confirmation instantly.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Real-time calendar sync
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Instant confirmations
                  </span>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <MessageSquare className="w-8 h-8 text-primary-foreground" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium text-primary">Step 4</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <h2 className="text-2xl font-bold mb-4">You Get a Full Summary</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  After each call, you receive a detailed summary including the full transcription, caller information, and any actions taken. Review everything from your dashboard—no guesswork needed.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Full transcriptions
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    AI summaries
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                <Shield className="w-10 h-10 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Data Privacy & GDPR Compliance
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  We take data protection seriously. CallChat is fully GDPR compliant and designed with privacy at its core.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <h4 className="font-semibold mb-2">EU Data Centers</h4>
                    <p className="text-sm text-muted-foreground">
                      All data is stored on servers located in the European Union.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <h4 className="font-semibold mb-2">End-to-End Encryption</h4>
                    <p className="text-sm text-muted-foreground">
                      Call data is encrypted in transit and at rest using 256-bit encryption.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <h4 className="font-semibold mb-2">No Third-Party Sharing</h4>
                    <p className="text-sm text-muted-foreground">
                      Your customer data is never sold or shared with third parties.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <h4 className="font-semibold mb-2">Data Retention Control</h4>
                    <p className="text-sm text-muted-foreground">
                      You control how long call data is retained and can delete it anytime.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
};

export default HowItWorks;
